import type { Vec3 } from '../lib/math';

// The star names of the pre-Islamic Arabs, as the classical scholars recorded them: Ahillah's own list
// (data-src/arabian), compiled from Ibn Qutaybah's Kitāb al-Anwāʾ, al-Marzūqī, Ibn Sīdah, the lexicons and the
// classical star catalogues, with the stars identified after Kunitzsch, and converted by scripts/build-arabian.py. Every
// name has its sources in the research data, though the app shows none (item 26); names are fully vocalised in pause.

export interface ArabicName {
  en: string;
  ar: string;
  tr: string;
  /** Other names the sources give for the same stars. */
  also?: { ar: string; tr: string }[];
}

/** A figure point: a Hipparcos number, an outline point [RA in hours, Dec in degrees], or "DSO:M45". */
export type FigurePoint = number | [number, number] | string;

export interface ArabicFigure extends ArabicName {
  id: string;
  kind: 'constellation' | 'asterism';
  lines: FigurePoint[][];
  /** The stars of a lunar station: its number, 1 to 28. */
  manzil?: number;
}

export interface ArabicSky {
  figures: ArabicFigure[];
  /** Hipparcos number → Arabic names of that star. */
  stars: Record<string, ArabicName[]>;
  lunar: { names: ArabicName[]; extent: number[]; anchorHip: number; offset: number; native: string };
}

export const ARABIC_INTRO =
  'The Arabs of the peninsula had their own names for the stars and star groups before Greek astronomy reached them. The classical scholars of the anwāʾ recorded these names.';

/** Whether a name's meaning is known: the list says "meaning uncertain" where the sources leave it open. */
export const knownMeaning = (en: string) => !/^meaning uncertain/i.test(en);

let cache: Promise<ArabicSky> | null = null;
let loaded: ArabicSky | null = null;
export function loadArabicSky(): Promise<ArabicSky> {
  cache ??= fetch(`${import.meta.env.BASE_URL}data/sky-arabian.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`sky-arabian.json: ${r.status}`);
      return r.json() as Promise<ArabicSky>;
    })
    .then((j) => (loaded = j))
    .catch((e) => {
      cache = null;
      throw e;
    });
  return cache;
}

/** The data if it has already loaded (for render loops). */
export const arabicSky = () => loaded;

// ---------- Lunar stations (manāzil al-qamar) ----------
// In Ibn Qutaybah's order, from al-Sharaṭān. The classical astronomers divided the ecliptic into 28 equal
// parts; here the division is fixed to the stars, placed so that each part is centred as nearly as possible
// on its own station's stars (scripts/build-arabian.py), and held by an anchor star in station 1. Both the
// anchor and the Moon are handled in the J2000 ecliptic frame, which gives the same differences as the
// ecliptic of date.

const EPS = (23.4392911 * Math.PI) / 180;
const COS_E = Math.cos(EPS), SIN_E = Math.sin(EPS);
export const STATION_WIDTH = 360 / 28;

/** J2000 equatorial unit vector → J2000 ecliptic longitude and latitude (degrees). */
export function eclipticOf(v: Vec3): { lon: number; lat: number } {
  const y = v[1] * COS_E + v[2] * SIN_E;
  const z = -v[1] * SIN_E + v[2] * COS_E;
  const lon = (Math.atan2(y, v[0]) * 180) / Math.PI;
  return { lon: (lon + 360) % 360, lat: (Math.asin(Math.max(-1, Math.min(1, z))) * 180) / Math.PI };
}

/** J2000 ecliptic longitude and latitude (degrees) → J2000 equatorial unit vector. */
export function fromEcliptic(lon: number, lat: number): Vec3 {
  const l = (lon * Math.PI) / 180, b = (lat * Math.PI) / 180;
  const x = Math.cos(b) * Math.cos(l), ye = Math.cos(b) * Math.sin(l), ze = Math.sin(b);
  return [x, ye * COS_E - ze * SIN_E, ye * SIN_E + ze * COS_E];
}

/** Ecliptic longitude where the first station begins. */
export const stationZero = (sky: ArabicSky, anchor: Vec3) => eclipticOf(anchor).lon - sky.lunar.offset;

/** Which station (0–27) a direction is in, and how far through it (0–1). */
export function stationOf(sky: ArabicSky, anchor: Vec3, dir: Vec3): { index: number; frac: number } {
  const x = (((eclipticOf(dir).lon - stationZero(sky, anchor)) % 360) + 360) % 360;
  const index = Math.floor(x / STATION_WIDTH) % 28;
  return { index, frac: x / STATION_WIDTH - index };
}
